

import { useCallback, useMemo } from "react";
import { useMetaTableOptional } from "@/providers/MetaTableProvider";
import { useFilters } from "@/providers/FiltersProvider";
import { useFields, useFieldsOptional } from "@/providers/FieldsProvider";
import { parseFiltersApiRequest } from "@/lib/utils/parser";
import { flatLaneMatchesTree, flatRowsReadAlike, sameFlatRows } from "@/lib/utils/condition-converters";
import { hasNonEmptyConditions } from "@/components/ConditionBuilder/condition-utils";
import type { BulkSelection } from "@/types/BulkSelection";
import type { Field } from "@/types/Field";
import type { ConditionGroup } from "@/types/EventRule";
import type { Filter } from "@/types/Filter";
import { applyGroupScope } from "@/lib/guests/groupScope";

/**
 * Thrown when an "all matching" bulk action runs under an active
 * nested-conditions filter whose flat derivation does not mean the same thing
 * (an OR tree has none — the empty list would act on EVERY guest; `≠ A, B` or a
 * range has one the backend reads wider — see `flatLaneMatchesTree`).
 * Callers surface this via their normal error toast (message is an
 * `errors`-namespace key). It carries the tree for a caller that CAN take
 * one (PSendEmail's "Schedule instead" hands it to the workflow wizard).
 */
export class AdvancedConditionsUnsupportedError extends Error {
  readonly conditions?: ConditionGroup;
  constructor(conditions?: ConditionGroup) {
    super("allMatchingFilterUnsupported");
    this.name = "AdvancedConditionsUnsupportedError";
    this.conditions = conditions;
  }
}

type ActiveFilterLike = { filter?: Filter[]; conditions?: ConditionGroup } | null | undefined;

/** Tables whose list is fetched by the guest TREE while their bulk actions
 *  send the FLAT rows. Every other table lists and acts on one lane (flat for
 *  registrants, companions, check-in logs, sent mails, the event list; the
 *  tree for speakers and sponsors), and keeps the old rule: only a tree with
 *  no flat rows at all is refused, because the empty list would act on every
 *  row. */
const TREE_LISTED_TABLES = new Set(["guest"]);

/** Does the action send every row the list sends, read the same way? Both run
 *  the same parser over the same fields — the list keeping a row whose key
 *  resolves to no field (a deleted field: the backend refuses it), the action
 *  dropping it, so the action would cover the whole list. Compared lane
 *  against lane, not against the input: the parser expands a multi-pick row
 *  (`= [a, b]`) into one row per value on both. And the export and bulk e-mail
 *  compile the rows in main-consumer, which must read each operand as the
 *  list's Java lane does (`flatRowsReadAlike`). */
function flatRowsAllResolve(rows: Filter[], fields: Field[]): boolean {
  const kept = rows.filter((r) => r?.key !== "archived");
  if (!kept.every((r) => typeof r?.key === "string" && r.key !== "")) return false;
  const sent = parseFiltersApiRequest(kept.map((r) => ({ ...r })), fields);
  const listed = parseFiltersApiRequest(kept.map((r) => ({ ...r })), fields, { onUnresolvedKey: "keep" });
  return sent.length === listed.length && flatRowsReadAlike(listed, fields);
}

/**
 * May an "all matching" action run under this filter state? On a guest table:
 * true when there is no tree and every flat row reaches the action (list and
 * action both use the flat rows), or when the flat rows the action sends mean
 * exactly the tree the list shows — see `flatLaneMatchesTree`.
 */
export function allMatchingFilterIsSafe(
  state: ActiveFilterLike,
  allFieldsUnfiltered: Field[] | undefined,
  tableType: string = "guest",
): boolean {
  if (!TREE_LISTED_TABLES.has(tableType)) {
    return !(hasNonEmptyConditions(state?.conditions) && (state?.filter?.length ?? 0) === 0);
  }
  const fields = allFieldsUnfiltered ?? [];
  if (!hasNonEmptyConditions(state?.conditions)) return flatRowsAllResolve(state?.filter ?? [], fields);
  return flatLaneMatchesTree(state?.conditions, fields, state?.filter ?? []);
}

/**
 * The popups that build their OWN rows from a filter (bulk edit's filter
 * scope, the export) and send those rows instead of the filter: do the rows
 * they seeded mean the filter the list shows? The table check above, AND row
 * for row against what the popup will send — a row its seed lost (an
 * unresolved key, a field hidden from a CUSTOM role) widens the job to guests
 * the list never showed, with or without a tree.
 */
export function popupSeedMatchesFilter(
  state: ActiveFilterLike,
  seededRows: Filter[],
  allFieldsUnfiltered: Field[] | undefined,
  tableType: string = "guest",
): boolean {
  const fields = allFieldsUnfiltered ?? [];
  if (TREE_LISTED_TABLES.has(tableType) && hasNonEmptyConditions(state?.conditions)) {
    return flatLaneMatchesTree(state?.conditions, fields, seededRows);
  }
  return sameFlatRows(state?.filter ?? [], seededRows, fields);
}

/** Why "select all N matching" is not offered, or `null` when it is. */
export type AllMatchingRefusal = "filter" | "duplicates" | null;

/**
 * The same answer for the affordances that ENTER all-matching mode (the
 * banner's "select all N", the group header's "select all N of this group"):
 * they show the offer disabled instead of letting every action refuse.
 * Duplicate mode lists the duplicates while every action sends the filter,
 * so "all N" there would mean every guest the filter matches.
 * The banner renders in every table, some of them outside a FieldsProvider —
 * hence the optional read; no fields means a tree is refused, never a crash.
 * The table's type and mode come from the caller, which already reads them.
 */
export function useAllMatchingRefusal({
  tableType,
  duplicateMode,
}: {
  tableType: string;
  duplicateMode: boolean;
}): AllMatchingRefusal {
  const { activeFilterState } = useFilters();
  const allFieldsUnfiltered = useFieldsOptional()?.allFieldsUnfiltered;
  const duplicates = !!duplicateMode;
  return useMemo(() => {
    if (duplicates) return "duplicates";
    return allMatchingFilterIsSafe(activeFilterState, allFieldsUnfiltered as Field[] | undefined, tableType)
      ? null
      : "filter";
  }, [activeFilterState, allFieldsUnfiltered, tableType, duplicates]);
}

export function useAllMatchingAllowed(table: { tableType: string; duplicateMode: boolean }): boolean {
  return useAllMatchingRefusal(table) === null;
}

/**
 * Builds a `BulkSelection` payload for the current table state.
 *
 * - When `selectionMode === "all-matching"`, returns a `filter` payload using the active
 *   filter set, search term, archived toggle, and excludeIds from the provider.
 * - Otherwise returns an `ids` payload with the ticked rows.
 *
 * Pass `{ forceMode: "filter" }` to override the upstream selection state — used by
 * the Bulk Edit modal where the user picks scope explicitly via a toggle instead of
 * inheriting it from the table's selection banner. `forceMode: "ids"` does the
 * symmetric override for that modal's "selected only" branch.
 *
 * The filter payload goes through `parseFiltersApiRequest` so additional-field keys are
 * prefixed before hitting the backend — same transform every other API path uses.
 */
export function useBulkSelectionPayload(): (
  opts?: { forceMode?: "filter" | "ids" },
) => BulkSelection {
  const meta = useMetaTableOptional();
  const { activeFilterState } = useFilters();
  const { allFieldsUnfiltered } = useFields();

  return useCallback((opts?: { forceMode?: "filter" | "ids" }): BulkSelection => {
    const selectionMode = meta?.selectionMode ?? "none";
    const selectedRows = meta?.selectedRows ?? [];
    const excludeIds = meta?.excludeIds ?? [];
    const search = meta?.search ?? "";
    const archived = !!meta?.archived;
    const useFilterMode =
      opts?.forceMode === "filter" ||
      (opts?.forceMode !== "ids" && selectionMode === "all-matching");
    // Invariant, checked on every build: a group scope only ever rides an
    // all-matching selection (the provider enters both in one commit and
    // clears both together). Anything else is a state the table cannot
    // explain, so it is said out loud in development and ignored.
    const scope = meta?.groupScope ?? null;
    if (scope && selectionMode !== "all-matching" && import.meta.env.DEV) {
      console.warn("useBulkSelectionPayload: groupScope set outside all-matching mode — ignored");
    }
    if (useFilterMode) {
      // Safety net (conditions migration): the bulk endpoints only understand
      // flat filters, while the list shows the rows the TREE matches. Proceed
      // only when the flat rows sent below compile to the same query as that
      // tree (flatLaneMatchesTree). An OR tree has no flat form at all (the
      // empty list would select every guest); `≠ A, B`, a range on one field,
      // a custom-field `≠` or `checkedIn = false` have one the backend reads
      // differently from the list — the action would touch guests the user
      // never saw. Every caller builds this at submit time, so a filter that
      // changed after the banner was shown is caught here too. An all-matching
      // selection made in duplicate mode would name every filter match, not
      // the duplicates on screen (the banner does not offer it there).
      if (
        (selectionMode === "all-matching" && meta?.isDuplicateMode) ||
        !allMatchingFilterIsSafe(activeFilterState, allFieldsUnfiltered as Field[], meta?.type)
      ) {
        throw new AdvancedConditionsUnsupportedError(activeFilterState?.conditions);
      }
      // `archived` is passed as a separate field below — strip any archived filter the
      // user explicitly added so the backend resolver doesn't end up with two competing
      // archived conditions (backend also derives archived from its own param).
      const rawFilters = (activeFilterState?.filter ?? []).filter(
        (f) => f.key !== "archived",
      );
      const parsed = parseFiltersApiRequest(
        rawFilters,
        // allFieldsUnfiltered: a bulk action over "all matching" must keep a
        // CUSTOM-role's filter on an allowlist-hidden field, else it would act on
        // MORE rows than the filter intended.
        (allFieldsUnfiltered as Field[]) ?? [],
      );
      // A group scope narrows the all-matching selection to ONE group. Its row
      // is appended AFTER the parser, never fed through it: the parser drops
      // keys it cannot resolve and strips empty operands, and a scope row
      // lost that way does not narrow the action — it widens it to the whole
      // filter. Only a live all-matching selection carries a scope; a popup
      // forcing filter mode on its own ("edit all matching" in bulk edit)
      // asked for the filter, not for the group.
      const filters =
        scope && selectionMode === "all-matching" ? applyGroupScope(parsed, scope) : parsed;
      // In ids-mode the user only sees rows on the current page, so `excludeIds`
      // from the all-matching banner is irrelevant here. When forcing filter
      // mode without an active all-matching banner we still want a clean
      // "everything matching" — drop excludeIds in that case.
      const exclude = selectionMode === "all-matching" ? excludeIds : [];
      // selectAll=true marks the explicit "every row matching current scope"
      // intent — either the user clicked the all-matching banner, or a popup
      // (PBulkEdit) forced filter mode. The backend uses this flag to allow an
      // empty `filters` list (no user-applied filter narrowing); without it,
      // empty filters are rejected to guard against accidentally-empty bodies.
      return {
        mode: "filter",
        filters,
        excludeIds: exclude,
        search,
        archived,
        selectAll: true,
      };
    }
    return { mode: "ids", ids: selectedRows };
  }, [meta, activeFilterState, allFieldsUnfiltered]);
}
